/**
 * ScoreboardHeader Component
 *
 * Branded header showing custom title.
 * Uses Chakra UI components with scoreboard CSS variables for theming.
 */

import * as React from 'react';
import { Flex, Heading, Image, Text, VStack } from '@chakra-ui/react';
import whiteHLogo from '../../../../../images/white_horizontal_logo.svg';
import { useScoreboardThemeContext } from '../context/ScoreboardThemeContext';

// =============================================================================
// Types
// =============================================================================

export interface ScoreboardHeaderProps {
  /** Override the title (defaults to branding.customTitle) */
  title?: string;
  /** Test type label (first subtitle line) */
  testTypeLabel?: string;
  /** Metric label (second subtitle line) */
  metricLabel?: string;
  /** Optional className for additional styling */
  className?: string;
  /** Test ID for e2e testing */
  'data-testid'?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * ScoreboardHeader - Branded header with title.
 *
 * @example
 * ```tsx
 * <ScoreboardHeader subtitle="Countermovement Jump" />
 * ```
 */
export const ScoreboardHeader: React.FC<ScoreboardHeaderProps> = ({
  title,
  testTypeLabel,
  metricLabel,
  className,
  'data-testid': testId = 'scoreboard-header',
}: ScoreboardHeaderProps): React.ReactElement => {
  const { branding } = useScoreboardThemeContext();

  const displayTitle = title ?? branding.customTitle;

  return (
    <Flex
      as='header'
      align='center'
      justify='space-between'
      py={4}
      mb={4}
      className={className}
      data-testid={testId}
      role='banner'
      aria-label='Scoreboard header'
    >
      <Flex align='center' gap={4}>
        {branding.showOrganizationLogo && branding.logoUrl && (
          <Image
            src={branding.logoUrl}
            alt='Organization logo'
            maxH='120px'
            maxW='180px'
            objectFit='contain'
            data-testid='scoreboard-logo'
            role='img'
          />
        )}
        <VStack align='flex-start' gap={1}>
          <Heading
            as='h1'
            m={0}
            fontSize={{
              base: '28px',
              md: 'var(--scoreboard-title-size, 48px)',
            }}
            fontWeight={800}
            textTransform='uppercase'
            letterSpacing='2px'
            color='var(--scoreboard-text-primary, #ffffff)'
            data-testid='scoreboard-title'
            id='scoreboard-title'
          >
            {displayTitle}
          </Heading>
          {testTypeLabel && (
            <Text
              as='p'
              fontSize='24px'
              fontWeight={600}
              textTransform='uppercase'
              letterSpacing='1px'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
              data-testid='scoreboard-subtitle-test-type'
            >
              {testTypeLabel}
            </Text>
          )}
          {metricLabel && (
            <Text
              as='p'
              fontSize='20px'
              fontWeight={500}
              textTransform='uppercase'
              letterSpacing='1px'
              color='var(--scoreboard-text-secondary, #b0b0b0)'
              data-testid='scoreboard-subtitle-metric'
            >
              {metricLabel}
            </Text>
          )}
        </VStack>
      </Flex>
      <Image
        src={whiteHLogo}
        alt='Hawkin Dynamics'
        h='56px'
        objectFit='contain'
        opacity={0.6}
        flexShrink={0}
      />
    </Flex>
  );
};
